import { useEffect, useRef } from 'react';
import { record } from 'rrweb';

const API_URL = process.env.REACT_APP_TAKEOVER_API_URL || 'http://localhost:8000';
const REPLAY_ENABLED =
  process.env.REACT_APP_TAKEOVER_REPLAY_ENABLED === '1' ||
  process.env.REACT_APP_TAKEOVER_REPLAY_ENABLED === 'true';

const FLUSH_INTERVAL_MS = 5000;
const MAX_EVENTS_PER_CHUNK = 100;
// Browsers refuse a keepalive fetch once the bodies of all in-flight
// keepalive requests in the document would exceed 64 KiB (fetch spec
// "inflight keepalive bytes"; Chrome enforces it). A desktop FullSnapshot
// chunk is ~166KB, so sending it with keepalive meant it never arrived and
// every desktop replay played blank. Gate on the serialized body's bytes,
// not the event count, and keep keepalive for small bodies so the pagehide
// flush still survives the tab closing.
const KEEPALIVE_MAX_INFLIGHT_BYTES = 64 * 1024;
let keepaliveInflightBytes = 0;

function byteLength(str) {
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(str).length;
  return unescape(encodeURIComponent(str)).length;
}

function makeId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function keyFor(sessionId, suffix) {
  return `takeover.replay.${suffix}.${sessionId}`;
}

function getRecordingId(sessionId) {
  const key = keyFor(sessionId, 'recordingId');
  const existing = sessionStorage.getItem(key);
  if (existing) return existing;
  const id = makeId();
  sessionStorage.setItem(key, id);
  return id;
}

function getNextSeq(sessionId) {
  const key = keyFor(sessionId, 'nextSeq');
  const raw = parseInt(sessionStorage.getItem(key) || '0', 10);
  return Number.isFinite(raw) ? raw : 0;
}

function setNextSeq(sessionId, seq) {
  sessionStorage.setItem(keyFor(sessionId, 'nextSeq'), String(seq));
}

export default function useReplayRecorder({ sessionId, sessionToken, currentView }) {
  const bufferRef = useRef([]);
  const seqRef = useRef(0);
  const recordingIdRef = useRef(null);
  const startedAtRef = useRef(null);
  const completedRef = useRef(false);
  const flushRef = useRef(null);

  useEffect(() => {
    if (!REPLAY_ENABLED || !sessionId || !sessionToken) return undefined;

    recordingIdRef.current = getRecordingId(sessionId);
    seqRef.current = getNextSeq(sessionId);
    startedAtRef.current = new Date().toISOString();
    completedRef.current = false;

    const flush = async () => {
      const events = bufferRef.current;
      if (!events.length) return;
      bufferRef.current = [];
      const seq = seqRef.current;
      seqRef.current += 1;
      setNextSeq(sessionId, seqRef.current);

      const body = JSON.stringify({
        recordingId: recordingIdRef.current,
        seq,
        events,
        startedAt: startedAtRef.current,
        endedAt: new Date().toISOString(),
        url: window.location.href,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
          devicePixelRatio: window.devicePixelRatio || 1,
        },
        userAgent: navigator.userAgent,
      });
      const bytes = byteLength(body);
      const keepalive = keepaliveInflightBytes + bytes <= KEEPALIVE_MAX_INFLIGHT_BYTES;
      if (keepalive) keepaliveInflightBytes += bytes;

      try {
        await fetch(`${API_URL}/api/takeover/sessions/${sessionId}/replay/chunks`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Session-Token': sessionToken,
          },
          keepalive,
          body,
        });
      } catch {
        /* replay is best-effort; never affect the seller flow */
      } finally {
        if (keepalive) keepaliveInflightBytes -= bytes;
      }
    };
    flushRef.current = flush;

    const stop = record({
      emit(event, isCheckout) {
        bufferRef.current.push(event);
        // rrweb emits exactly one FullSnapshot (type 2) per recording
        // start, plus one on every checkout. That snapshot is the only
        // frame the player can paint the initial DOM from — if its chunk
        // is ever dropped (e.g. an early effect re-run cancels the first
        // in-flight POST), the whole replay renders blank. Flush it
        // immediately in its own chunk so it lands before any teardown,
        // instead of waiting for the 5s interval / 100-event threshold.
        if (event.type === 2 || isCheckout) {
          void flush();
          return;
        }
        if (bufferRef.current.length >= MAX_EVENTS_PER_CHUNK) {
          void flush();
        }
      },
      // Re-emit a fresh FullSnapshot every 30s. Belt-and-suspenders: even
      // if the very first snapshot chunk is lost, the player self-heals
      // from the next checkpoint instead of staying blank forever.
      checkoutEveryNms: 30000,
      // SECURITY: mask ALL input values in the replay stream. The wizard
      // captures the seller's LinkedIn email/password/2FA (Step05), and an
      // unmasked replay writes those cleartext into S3 (takeover-replays/).
      // maskAllInputs:true redacts every input; the per-type map below is
      // belt-and-suspenders for the credential-bearing types.
      maskAllInputs: true,
      maskInputOptions: {
        color: false,
        date: false,
        'datetime-local': false,
        email: true,
        month: false,
        number: false,
        range: false,
        search: true,
        tel: true,
        text: true,
        time: false,
        url: true,
        week: false,
        textarea: true,
        select: false,
        password: true,
      },
      // MUST stay false on rrweb 1.1.3: inlining walks every stylesheet's
      // cssRules, and a cross-origin / not-yet-loaded sheet throws a
      // SecurityError mid-serialization that aborts the entire FullSnapshot
      // — rrweb emits the Meta event, then snapshot() throws, so no
      // type-2 FullSnapshot is ever produced and the replay renders blank.
      // (Confirmed live 2026-05-29: Meta present, FullSnapshot absent,
      // no snapshot-sized chunk, all POSTs 200.) The replay player still
      // loads the app's same-origin CSS <link>s, so playback stays styled.
      inlineStylesheet: false,
      recordCanvas: false,
      collectFonts: false,
    });

    const interval = setInterval(() => { void flush(); }, FLUSH_INTERVAL_MS);
    const onPageHide = () => { void flush(); };
    window.addEventListener('pagehide', onPageHide);

    return () => {
      window.removeEventListener('pagehide', onPageHide);
      clearInterval(interval);
      try { stop(); } catch { /* ignore */ }
      void flush();
    };
  }, [sessionId, sessionToken]);

  useEffect(() => {
    if (!REPLAY_ENABLED || !sessionId || !sessionToken || completedRef.current) return;
    if (currentView !== 'completed') return;
    completedRef.current = true;
    void (async () => {
      if (flushRef.current) await flushRef.current();
      try {
        await fetch(`${API_URL}/api/takeover/sessions/${sessionId}/replay/complete`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Session-Token': sessionToken,
          },
          body: JSON.stringify({ recordingId: recordingIdRef.current }),
        });
      } catch {
        /* replay completion is best-effort */
      }
    })();
  }, [sessionId, sessionToken, currentView]);
}
